import { isDate } from "date-fns";
import { isEqual, isObject, merge, transform } from "lodash";
import { dateToyyyyMMdd } from "./date";
import { FormikProps } from "formik";
import { replaceEmptyString } from "../api/util";

export const handleFormikChange = <U>(
  formik: FormikProps<U>,
  name: keyof U,
  value: U[Extract<keyof U, typeof name>],
) => {
  formik.handleBlur({
    target: {
      name,
    },
  });
  formik.handleChange({
    target: {
      name,
      value,
    },
  });
};

export function getFieldValue<T>(field: T, rowData: any) {
  const accessors = (field as unknown as string).split(".");
  const data = accessors.reduce((prev, curr) => {
    return prev ? prev[curr] : prev;
  }, rowData);

  return data;
}

export function getInitialValues<U, V = U>(config: FormConfig<U, V>) {
  let initialValues = Object.fromEntries(
    config.form
      .filter((c: any) => c.initialValue !== undefined)
      .map((c: any) => {
        return [c.id, c.initialValue];
      }),
  );
  if (config.lockedValues) {
    const lockedValues = config.lockedValues.reduce((prev, curr) => {
      prev[curr.id] = curr.value;
      return prev;
    }, {});
    initialValues = merge(initialValues, lockedValues);
  }
  return initialValues;
}

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
export function difference(base, object) {
  function changes(object, base) {
    return transform(object, function (result: any, value, key) {
      if (!isEqual(value, base[key])) {
        result[key] =
          isObject(value) && isObject(base[key])
            ? changes(value, base[key])
            : value;
      }
    });
  }
  return changes(object, base);
}

export const getObjectDiff = (obj1, obj2, compareRef = false) => {
  const changedKeys = Object.keys(obj1).reduce((result, key) => {
    if (!obj2.hasOwnProperty(key)) {
      result.push(key);
    } else if (isEqual(obj1[key], obj2[key])) {
      const resultKeyIndex = result.indexOf(key);

      if (compareRef && obj1[key] !== obj2[key]) {
        result[resultKeyIndex] = `${key} (ref)`;
      } else {
        result.splice(resultKeyIndex, 1);
      }
    }
    return result;
  }, Object.keys(obj2));
  return changedKeys.reduce<any>((prev, curr) => {
    prev[curr] = obj2[curr];
    return prev;
  }, {});
};

/**
 * Takes form-values that have been prefiltered to the ones that
 * have changed, and returns patch operations for those.
 * @param values
 * @returns
 */
interface Ops {
  op: "replace" | "remove";
  path: string;
  value: string;
}
export function getPatchOperations(values: any) {
  const ops: Ops[] = Object.keys(values)
    .map((key): Ops => {
      const value = values[key];
      const isAddress = key === "adresseDar" || key === "andenAdresse";

      if (key === "periodeTilDatoInklusiv" && value === null) {
        return {
          op: "replace",
          path: "/periodeTilDatoInklusiv",
          value: "9999-12-31",
        };
      }

      return {
        op: value === undefined || value === null ? "remove" : "replace",
        path: "/" + (isAddress ? "adresse" : key),
        value: isDate(value) ? dateToyyyyMMdd(value) : value,
      };
    })
    .filter((op) => !(op.path === "/adresse" && !op.value)) as Ops[];

  return replaceEmptyString(ops);
}
export function mergeExistingValuesIntoForm<U, V = U>(
  editConfig: FormConfig<U, V>,
  rowData: any,
) {
  return editConfig.form.map((c) => {
    let data = getFieldValue(c.id, rowData);
    if (rowData.adresse) {
      if (c.id === "adresseDar" && rowData.adresse.darAdresseId !== null) {
        data = rowData.adresse;
      }
      if (c.id === "andenAdresse" && rowData.adresse.darAdresseId === null) {
        data = rowData.adresse;
      }
    }
    return {
      ...c,
      initialValue: data ? data : c.initialValue,
    };
  });
}
